import React from 'react';

function CellInput({ column, value, onChange }) {
  if (column.type === 'select') {
    return (
      <select className="rb-cell-input rb-cell-select" value={value || ''} onChange={(e) => onChange(e.target.value)}>
        <option value="" />
        {column.options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  }
  return (
    <input
      className="rb-cell-input"
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder={column.placeholder}
    />
  );
}

/** Dynamic-row table: user can add and remove rows freely (up to field.maxRows, if set). */
export function TableField({ field, value, onChange }) {
  const rows = value && value.length ? value : [];

  const updateCell = (rowIdx, key, cellValue) => {
    const next = rows.map((r, i) => (i === rowIdx ? { ...r, [key]: cellValue } : r));
    onChange(next);
  };

  const addRow = () => onChange([...rows, {}]);
  const removeRow = (idx) => onChange(rows.filter((_, i) => i !== idx));
  const atMax = field.maxRows && rows.length >= field.maxRows;

  return (
    <div className="rb-table-wrap">
      <table className="rb-table">
        <thead>
          <tr>
            {field.columns.map((col) => (
              <th key={col.key} style={{ width: col.width ? `${col.width}%` : undefined }}>
                {col.label}
              </th>
            ))}
            <th className="rb-table-actioncol" aria-hidden="true" />
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td colSpan={field.columns.length + 1} className="rb-table-empty">
                No rows yet — add the first one below.
              </td>
            </tr>
          )}
          {rows.map((row, idx) => (
            <tr key={idx}>
              {field.columns.map((col) => (
                <td key={col.key}>
                  <CellInput column={col} value={row[col.key]} onChange={(v) => updateCell(idx, col.key, v)} />
                </td>
              ))}
              <td className="rb-table-actioncol">
                <button type="button" className="rb-icon-btn rb-icon-btn-danger" onClick={() => removeRow(idx)} title="Remove row">
                  ✕
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button type="button" className="rb-add-row-btn" onClick={addRow} disabled={atMax}>
        + Add row
      </button>
      {atMax && <span className="rb-hint">Maximum of {field.maxRows} rows.</span>}
    </div>
  );
}

/** Fixed-row table: the rows themselves (labels) are defined by the schema and cannot
 *  be added/removed — only the editable cells change. Used for score summaries, level
 *  distributions, and pillar-by-generation grids that mirror the printed template. */
export function FixedTableField({ field, value, onChange }) {
  const rows = field.rows;
  const data = value && value.length ? value : rows.map(() => ({}));

  const updateCell = (rowIdx, key, cellValue) => {
    const next = data.map((r, i) => (i === rowIdx ? { ...r, [key]: cellValue } : r));
    onChange(next);
  };

  return (
    <div className="rb-table-wrap">
      <table className="rb-table rb-table-fixed">
        <thead>
          <tr>
            {field.columns.map((col) => (
              <th key={col.key}>{col.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((rowDef, idx) => {
            const isObj = typeof rowDef === 'object';
            const label = isObj ? rowDef.label : rowDef;
            const bold = isObj && rowDef.bold;
            return (
              <tr key={idx} className={bold ? 'rb-row-emphasis' : undefined}>
                {field.columns.map((col) => {
                  if (col.type === 'label') {
                    return (
                      <td key={col.key} className="rb-cell-label">
                        {label}
                      </td>
                    );
                  }
                  const prefill = isObj && rowDef[col.key] !== undefined ? rowDef[col.key] : '';
                  const val = data[idx]?.[col.key] !== undefined ? data[idx][col.key] : prefill;
                  return (
                    <td key={col.key}>
                      <CellInput column={col} value={val} onChange={(v) => updateCell(idx, col.key, v)} />
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
